import { useState } from 'react'
import { Copy, Check, ThumbsUp, ThumbsDown } from 'lucide-react'
import clsx from 'clsx'

interface MessageActionBarProps {
  content: string
  role: 'user' | 'assistant'
  messageId: string
  feedback?: 'up' | 'down' | null
  onFeedback?: (messageId: string, rating: 'up' | 'down' | null) => void
}

export function MessageActionBar({
  content,
  role,
  messageId,
  feedback,
  onFeedback,
}: MessageActionBarProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API may fail in some contexts — fail silently
    }
  }

  const handleFeedback = (rating: 'up' | 'down') => {
    if (!onFeedback) return
    // Toggle: clicking the same rating clears it
    onFeedback(messageId, feedback === rating ? null : rating)
  }

  return (
    <div className="mt-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
      {/* Copy */}
      <button
        onClick={handleCopy}
        className="rounded p-1 text-subtle-foreground transition hover:bg-foreground/[0.10] hover:text-muted-foreground"
        aria-label={copied ? 'Copied to clipboard' : 'Copy message to clipboard'}
        title={copied ? 'Copied!' : 'Copy'}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      </button>

      {/* Thumbs up/down — assistant messages only */}
      {role === 'assistant' && onFeedback && (
        <>
          <button
            onClick={() => handleFeedback('up')}
            className={clsx(
              'rounded p-1 transition',
              feedback === 'up'
                ? 'text-success-foreground bg-success-soft'
                : 'text-subtle-foreground hover:bg-foreground/[0.10] hover:text-muted-foreground',
            )}
            aria-label="Rate as helpful"
            aria-pressed={feedback === 'up'}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => handleFeedback('down')}
            className={clsx(
              'rounded p-1 transition',
              feedback === 'down'
                ? 'text-destructive bg-destructive-soft'
                : 'text-subtle-foreground hover:bg-foreground/[0.10] hover:text-muted-foreground',
            )}
            aria-label="Rate as not helpful"
            aria-pressed={feedback === 'down'}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </div>
  )
}
